import { apiGet, apiGetBlob, apiGetMeta, apiPatch, apiPost, apiPostMultipart, apiPostWithHeaders, apiPut } from './api-client.js';

/**
 * Typed wrappers over `GET /files*` (API_CONTRACT.md §13.2, shapes VERIFIED against a
 * running server — see `docs/01-architecture/VERIFIED_RESPONSES.md`). This is the ONLY
 * file that knows these paths — routes/components call these functions, never `apiGet`
 * directly, matching `lib/auth.ts`'s convention.
 */

export interface FileOrgUnitRef {
  id: string;
  nameNe: string;
  nameEn: string;
}

/**
 * `GET /files` — a register row. `subject` is `null` exactly when `subjectRedacted` is
 * true (the caller's clearance is below the file's, and the server has already blanked
 * it) — never try to reconstruct a redacted subject client-side.
 */
export interface FileListRow {
  id: string;
  trackingCode: string;
  subject: string | null;
  subjectRedacted: boolean;
  statusCode: string;
  confidentialityLevel: string;
  currentHolderUserId: string | null;
  section: FileOrgUnitRef;
  registeredAt: string;
  registeredDateBs: string;
  dueAt: string | null;
  isOverdue: boolean;
  archivedAt: string | null;
}

export interface OffsetPageMeta {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

export interface ListFilesParams {
  page?: number;
  pageSize?: number;
  /** Whitelisted server-side (API_CONTRACT §9.1) — an unlisted value is `422 VALIDATION_FAILED`, never silently dropped. */
  sort?: 'registeredAt' | '-registeredAt' | 'dueAt' | '-dueAt' | 'trackingCode' | '-trackingCode';
  statusCode?: string;
  confidentialityLevel?: string;
  isOverdue?: boolean;
  /**
   * `sectionId`, `categoryId`, `holderUserId`, `bsMonth`/`bsFiscalYear` and
   * `registeredFrom/To` are all named in API_CONTRACT.md's prose table but the actual
   * Zod schema on the running server (`file-routes.ts`'s `registerQuerySchema`) accepts
   * only `sectionId, statusCode, categoryId, confidentialityLevel, holderUserId,
   * isOverdue, bsYear, q` — no `bsMonth`, no `bsFiscalYear`, no date-range filter. This
   * client only exposes the filters the server actually accepts; the gap is reported in
   * the handback rather than sending a filter the server would 422 on.
   */
  bsYear?: number;
  q?: string;
}

function buildQuery(params: Record<string, string | number | boolean | undefined>): string {
  const usp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '') continue;
    usp.set(key, String(value));
  }
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

export async function listFiles(
  params: ListFilesParams = {},
  signal?: AbortSignal,
): Promise<{ rows: FileListRow[]; page: OffsetPageMeta }> {
  const query = buildQuery({
    page: params.page,
    pageSize: params.pageSize,
    sort: params.sort,
    statusCode: params.statusCode,
    confidentialityLevel: params.confidentialityLevel,
    isOverdue: params.isOverdue,
    bsYear: params.bsYear,
    q: params.q,
  });
  const { data, meta } = await apiGetMeta<FileListRow[]>(`/files${query}`, signal);
  const page = (meta?.page as OffsetPageMeta | undefined) ?? {
    page: params.page ?? 1,
    pageSize: params.pageSize ?? data.length,
    totalItems: data.length,
    totalPages: 1,
  };
  return { rows: data, page };
}

export interface FileApplicant {
  fullName: string;
  phone: string;
  email: string | null;
  addressText: string | null;
}

export interface FileDartaChalani {
  dartaNumber: string | null;
  chalaniNumber: string | null;
  registerDate: string | null;
  linkedAt: string | null;
  linkedBy: string | null;
}

export interface AttachmentRow {
  id: string;
  fileId: string;
  originalFilename: string;
  mimeType: string;
  byteSize: number;
  confidentialityLevel: string;
  scanState: string;
  attached: boolean;
  uploadedBy: string | null;
  uploadedAt: string;
  archivedAt: string | null;
}

/** `GET /files/{id}` — everything `FileListRow` has, plus the fields only the detail view needs. */
export interface FileDetail {
  id: string;
  trackingCode: string;
  subject: string | null;
  categoryId: string | null;
  confidentialityLevel: string;
  statusCode: string;
  currentHolderUserId: string | null;
  section: FileOrgUnitRef;
  registeredBy: string | null;
  registeredAt: string;
  registeredDateBs: string;
  dueAt: string | null;
  isOverdue: boolean;
  dartaChalani: FileDartaChalani;
  applicant: FileApplicant | null;
  attachments: AttachmentRow[];
  archivedAt: string | null;
  /** The optimistic-concurrency token. Every write against this file must echo it back as `expectedVersion`. */
  version: number;
}

export function getFile(fileId: string, signal?: AbortSignal): Promise<FileDetail> {
  return apiGet<FileDetail>(`/files/${fileId}`, signal);
}

export function listFileAttachments(fileId: string, signal?: AbortSignal): Promise<AttachmentRow[]> {
  return apiGet<AttachmentRow[]>(`/files/${fileId}/attachments`, signal);
}

/**
 * `POST /files` (API_CONTRACT.md §13.2, VERIFIED_RESPONSES.md) — file intake, SCREEN_SPECS.md
 * I7. **There is no `confidentialityLevel` field on this request and its absence is the
 * control (D-2)** — sending one is `422 unknown_field`, so this type deliberately has no
 * such property for a caller to accidentally populate. `phone` must match
 * `^(\+977)?9[678][0-9]{8}$`; `sectionId` outside the actor's scope is a `422`, not a `403`.
 * `Idempotency-Key` is REQUIRED (AC-1) — minted once per registration attempt by the
 * caller (R-035's rule applies here exactly as it does to movements) and reused across
 * retries of the SAME submission.
 */
export interface RegisterFileApplicantInput {
  fullName: string;
  phone: string;
  email?: string | null;
  addressText?: string | null;
  preferredLocale?: 'ne' | 'en';
}

export interface RegisterFileInput {
  subject: string;
  sectionId: string;
  categoryId?: string | null;
  applicant: RegisterFileApplicantInput;
  darta?: {
    dartaNumber?: string | null;
    chalaniNumber?: string | null;
    registerDate?: string | null;
  } | null;
}

/** VERIFIED_RESPONSES.md — the receipt a citizen leaves intake with. `qrToken` never appears anywhere else. */
export interface RegisterFileResult {
  id: string;
  trackingCode: string;
  qrToken: string;
  registeredDateBs: string;
  statusCode: string;
  confidentialityLevel: string;
  dueAt: string | null;
}

export function registerFile(
  input: RegisterFileInput,
  idempotencyKey: string,
  signal?: AbortSignal,
): Promise<RegisterFileResult> {
  return apiPostWithHeaders<RegisterFileResult>('/files', input, { 'Idempotency-Key': idempotencyKey }, signal);
}

/** `PATCH /files/{id}` (§13.2) — subject, category, due date. `expectedVersion` required. */
export interface UpdateFileInput {
  expectedVersion: number;
  subject?: string;
  categoryId?: string | null;
  dueAt?: string | null;
}

export function updateFile(fileId: string, input: UpdateFileInput, signal?: AbortSignal): Promise<{ version: number }> {
  return apiPatch<{ version: number }>(`/files/${fileId}`, input, signal);
}

/**
 * `POST /files/{id}/classification` (§13.2, DATA_MODEL.md §15.1) — raise or lower a file's
 * confidentiality level. `section`+ may raise; only `ministry`+ may lower (server-enforced,
 * re-checked in-transaction — this client does not attempt to pre-filter the level options
 * by direction, since it cannot know the actor's own clearance rank against every level
 * without a `/reference/confidentiality-levels` endpoint, which is not mounted in this
 * build — see this agent's handback).
 */
export interface ClassifyFileInput {
  confidentialityLevel: string;
  reason: string;
  expectedVersion: number;
}

export function classifyFile(
  fileId: string,
  input: ClassifyFileInput,
  signal?: AbortSignal,
): Promise<{ version: number; confidentialityLevel: string }> {
  return apiPost<{ version: number; confidentialityLevel: string }>(`/files/${fileId}/classification`, input, signal);
}

/**
 * `PUT /files/{id}/darta-chalani` (§13.2) — A-004's "linked later". Audited, but explicitly
 * NOT a movement (no timeline entry). All three fields are independently optional/nullable
 * — omitting a field leaves it unchanged; sending `null` clears it.
 */
export interface LinkDartaChalaniInput {
  dartaNumber?: string | null;
  chalaniNumber?: string | null;
  registerDate?: string | null;
  expectedVersion: number;
}

export function linkDartaChalani(fileId: string, input: LinkDartaChalaniInput, signal?: AbortSignal): Promise<{ version: number }> {
  return apiPut<{ version: number }>(`/files/${fileId}/darta-chalani`, input, signal);
}

/** `GET /files/{id}/qr` (§13.2) — the QR payload for printing the citizen's receipt. */
export interface FileQrPayload {
  trackingCode: string;
  qrToken: string;
  /** `/t#<token>` — the fragment-only path §14.1 relies on so the token never reaches a server log. */
  qrFragmentPath: string;
}

export function getFileQr(fileId: string, signal?: AbortSignal): Promise<FileQrPayload> {
  return apiGet<FileQrPayload>(`/files/${fileId}/qr`, signal);
}

/**
 * `POST /files/{id}/attachments` (§13.4, §10) — multipart upload. This deployment scans
 * synchronously inside the transaction (`file-service.ts`'s own comment: "this deployment
 * scans synchronously ... the row is already clean"), so a successful `201` always answers
 * `scanState: 'clean', attached: true` today — there is no server-observed `pending` state
 * to poll for in THIS build. The type still carries the full `scanState` union rather than
 * a boolean, because §10's documented contract is an asynchronous scanner and a future
 * deployment may make the "pending, poll `GET .../attachments`" path real; a caller that
 * only checks `attached === true` handles both worlds identically. A file that fails the
 * scan never reaches `201` at all — it fails the request with `ATTACHMENT_NOT_CLEAN`
 * (`retryable: true`), handled as a distinct, non-generic upload-error state by the caller.
 */
export interface UploadAttachmentResult {
  id: string;
  fileId: string;
  originalFilename: string;
  mimeType: string;
  byteSize: number;
  scanState: string;
  attached: boolean;
}

export function uploadAttachment(
  fileId: string,
  file: File,
  confidentialityLevel: string | undefined,
  signal?: AbortSignal,
): Promise<UploadAttachmentResult> {
  const formData = new FormData();
  formData.append('file', file, file.name);
  const query = confidentialityLevel ? `?confidentialityLevel=${encodeURIComponent(confidentialityLevel)}` : '';
  return apiPostMultipart<UploadAttachmentResult>(`/files/${fileId}/attachments${query}`, formData, signal);
}

/**
 * `POST /attachments/{id}/archive` (§13.4) — soft-withdraw only (A-006: never a hard
 * delete). VERIFIED against `file-service.ts`'s own return type: `{id, archived}`, not the
 * `{archivedAt}` a first read of the contract's prose suggests.
 */
export function archiveAttachment(attachmentId: string, signal?: AbortSignal): Promise<{ id: string; archived: boolean }> {
  return apiPost<{ id: string; archived: boolean }>(`/attachments/${attachmentId}/archive`, undefined, signal);
}

/**
 * The user's own words: "not view option of document." Every attachment could only be
 * downloaded — a save-to-disk-then-reopen round trip for every page an officer needs to
 * read. This is the inline-preview allowlist: **PDF and raster images only**, matched to
 * the coordinator's explicit security bar —
 *
 *   "Never render an untrusted document in a context that can execute. No `<iframe
 *   srcdoc>`, no HTML preview, no SVG rendered as markup — SVG is script-capable and these
 *   files arrive from the public."
 *
 * `image/svg+xml` is deliberately excluded even though it is technically a raster-adjacent
 * "image/*" type: SVG is XML that can carry `<script>`/event-handler content a citizen's
 * upload should never get to execute in a staff member's authenticated session. Anything
 * outside this allowlist (Word docs, zips, etc.) is download-only — never rendered inline.
 */
const PREVIEWABLE_IMAGE_MIME_TYPES: ReadonlySet<string> = new Set([
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'image/bmp',
]);

export type AttachmentPreviewKind = 'pdf' | 'image';

/**
 * `null` = not previewable — either the mime type is outside the allowlist above, or the
 * attachment fails the SAME fail-closed gate the download link already uses (`attached`
 * false, or `scanState !== 'clean'`): an attachment the malware scanner has not cleared is
 * not previewable any more than it is downloadable, and that must stay true even if a
 * future scanner makes `pending` a real, observed state in this deployment (`files.ts`'s
 * own comment on `UploadAttachmentResult`).
 */
export function attachmentPreviewKind(
  attachment: Pick<AttachmentRow, 'mimeType' | 'attached' | 'scanState'>,
): AttachmentPreviewKind | null {
  if (!attachment.attached || attachment.scanState !== 'clean') return null;
  if (attachment.mimeType === 'application/pdf') return 'pdf';
  if (PREVIEWABLE_IMAGE_MIME_TYPES.has(attachment.mimeType)) return 'image';
  return null;
}

/**
 * Fetches the attachment's bytes and re-wraps them in a `Blob` carrying the mime type
 * `GET /files/{id}/attachments` reported for it — the download endpoint itself always
 * answers `Content-Type: application/octet-stream` (a deliberate download-forcing
 * default the coordinator asked this feature to leave alone: it keeps
 * `Content-Disposition: attachment` the safe default for the plain download link), so an
 * inline `<embed>`/`<img>` given that raw response would refuse to render. This is a
 * client-side-only fix — no `api/` change — driven entirely by metadata this client
 * already has from a permission-checked call it already made.
 */
export async function fetchAttachmentPreviewBlob(
  attachmentId: string,
  mimeType: string,
  signal?: AbortSignal,
): Promise<Blob> {
  const raw = await apiGetBlob(`/attachments/${attachmentId}/download`, signal);
  return new Blob([raw], { type: mimeType });
}

export { ApiError, NetworkError } from './api-client.js';
